import { Indicator, IndicatorInput, IndicatorInput_InputType } from '../proto/generated/indicator'
import {
    CHART_PATTERNS_GROUP_NAME,
    CANDLESTICK_PATTERNS_GROUP_NAME,
    PATTERN_SUBCATEGORY_CANDLESTICK,
    PATTERN_SUBCATEGORY_CHART,
    isPatternGroupName,
} from '../assets/patternSubcategories'
import { SeriesOutput } from '../components/chartV2/chart.typings'
import {
    getPatternDrawingSettingsMap,
} from '../components/chartV2/drawingTools/chartPatternOverlayTool/chartPatternOverlay.constants'

/** Input code defined in Sirius candlestick pattern scripts (`LABEL_COLOR`). */
export const PATTERN_LABEL_COLOR_INPUT_CODE = 'LABEL_COLOR'

/** Trend filter input used by candlestick pattern detectors (UI + Nexus alerts). */
export const PATTERN_DETECT_TREND_BASED_ON_INPUT_CODE = 'DETECT_TREND_BASED_ON'

const DEPRECATED_PATTERN_INPUT_CODES = new Set(['LOOKBACK_BARS'])

/** Display-only / non-detection inputs — not sent to backend pattern evaluation. */
export const PATTERN_ALERT_OPTION_EXCLUDE_CODES = new Set([
    PATTERN_LABEL_COLOR_INPUT_CODE,
    ...DEPRECATED_PATTERN_INPUT_CODES,
])

export function normalizeIndicatorCode(code?: string): string {
    return (code ?? '').replace(/\W/g, '_').toUpperCase()
}

export function hasChartPatternOverlayOutput(indicator?: Indicator): boolean {
    return Boolean(
        indicator?.outputs?.some((output) => output.drawingType === 'chartPatternOverlay'),
    )
}

export function isPatternIndicator(indicator?: Indicator): boolean {
    return isPatternGroupName(indicator?.groupName) && hasChartPatternOverlayOutput(indicator)
}

export function findPatternIndicator(code: string, indicators: Indicator[]): Indicator | undefined {
    const normalized = normalizeIndicatorCode(code)
    return indicators.find(
        (ind) => normalizeIndicatorCode(ind.code) === normalized && isPatternIndicator(ind),
    )
}

export function isPatternIndicatorCode(code: string, indicators: Indicator[]): boolean {
    return Boolean(findPatternIndicator(code, indicators))
}

export function isChartPatternIndicator(indicator?: Indicator): boolean {
    if (!isPatternIndicator(indicator)) return false
    if (indicator!.groupName === CHART_PATTERNS_GROUP_NAME) return true
    if (indicator!.groupName === CANDLESTICK_PATTERNS_GROUP_NAME) return false
    const description = indicator!.description?.trim()
    if (description === PATTERN_SUBCATEGORY_CHART) return true
    if (description === PATTERN_SUBCATEGORY_CANDLESTICK) return false
    const inputCodes = new Set(indicator!.inputs.map((input) => input.code))
    return inputCodes.has('PATTERNS') || inputCodes.has('PRICE_TARGETS') || inputCodes.has('TREND_HEIGHT')
}

export function isCandlestickPatternIndicator(indicator?: Indicator): boolean {
    if (!isPatternIndicator(indicator)) return false
    if (indicator!.groupName === CANDLESTICK_PATTERNS_GROUP_NAME) return true
    if (indicator!.groupName === CHART_PATTERNS_GROUP_NAME) return false
    return !isChartPatternIndicator(indicator)
}

export function isChartPatternIndicatorCode(code: string, indicators: Indicator[]): boolean {
    return isChartPatternIndicator(findPatternIndicator(code, indicators))
}

export function isCandlestickPatternIndicatorCode(code: string, indicators: Indicator[]): boolean {
    return isCandlestickPatternIndicator(findPatternIndicator(code, indicators))
}

const PATTERN_INPUT_ORDER = [
    PATTERN_LABEL_COLOR_INPUT_CODE,
    PATTERN_DETECT_TREND_BASED_ON_INPUT_CODE,
] as const

/**
 * Build AlertPattern.options from chart series-group inputs so Nexus detection
 * matches the UI (e.g. DETECT_TREND_BASED_ON). Keys are sanitized; display-only
 * inputs (label color) are omitted. Indicator defaults fill missing values.
 */
export function buildPatternAlertOptionsFromInputValues(
    inputValues?: Record<string, string>,
    indicator?: Indicator,
    scriptInputs?: IndicatorInput[],
): Record<string, string> {
    const options: Record<string, string> = {}
    if (isCandlestickPatternIndicator(indicator)) {
        const fields = ensureCandlestickPatternConfigurationFields(indicator, scriptInputs)
        for (const field of fields) {
            const code = normalizeIndicatorCode(field.code)
            if (!code || PATTERN_ALERT_OPTION_EXCLUDE_CODES.has(code)) continue
            const def = String(field.defval ?? '').trim()
            if (def) options[code] = def
        }
    }
    for (const [rawKey, rawValue] of Object.entries(inputValues ?? {})) {
        const code = normalizeIndicatorCode(rawKey)
        if (!code || PATTERN_ALERT_OPTION_EXCLUDE_CODES.has(code)) continue
        const value = String(rawValue ?? '').trim()
        if (value) options[code] = value
    }
    return options
}

const orderPatternConfigurationFields = (fields: IndicatorInput[]): IndicatorInput[] =>
    [...fields].sort((a, b) => {
        const ai = PATTERN_INPUT_ORDER.indexOf(a.code as typeof PATTERN_INPUT_ORDER[number])
        const bi = PATTERN_INPUT_ORDER.indexOf(b.code as typeof PATTERN_INPUT_ORDER[number])
        const aRank = ai === -1 ? PATTERN_INPUT_ORDER.length : ai
        const bRank = bi === -1 ? PATTERN_INPUT_ORDER.length : bi
        return aRank - bRank
    })

export function getPatternConfigurationFields(
    indicator?: Indicator,
    scriptInputs?: IndicatorInput[],
): IndicatorInput[] {
    const filterFields = (fields: IndicatorInput[]) =>
        fields
            .filter((field) => !DEPRECATED_PATTERN_INPUT_CODES.has(field.code))
            .filter((field) => !isPatternIndicator(indicator) || field.type !== IndicatorInput_InputType.BOOLEAN)
            .map((field) => ({
                ...field,
                options: field.options ?? [],
            }))

    const catalogFields = filterFields(indicator?.inputs ?? [])
    const parsedScriptFields = filterFields(scriptInputs ?? [])

    if (catalogFields.length > 0 || parsedScriptFields.length > 0) {
        const byCode = new Map<string, IndicatorInput>()
        catalogFields.forEach((field) => byCode.set(field.code, field))
        parsedScriptFields.forEach((field) => {
            if (!byCode.has(field.code)) {
                byCode.set(field.code, field)
            }
        })
        return orderPatternConfigurationFields(Array.from(byCode.values()))
    }

    return []
}

export function ensureCandlestickPatternConfigurationFields(
    indicator?: Indicator,
    scriptInputs?: IndicatorInput[],
): IndicatorInput[] {
    const fields = getPatternConfigurationFields(indicator, scriptInputs)
    if (!isCandlestickPatternIndicator(indicator)) {
        return fields
    }
    if (fields.some(isPatternLabelColorInput)) {
        return fields
    }
    const bias = getCandlestickPatternBias(indicator)
    return orderPatternConfigurationFields([
        ...fields,
        {
            title: 'Label Color',
            code: PATTERN_LABEL_COLOR_INPUT_CODE,
            type: IndicatorInput_InputType.STRING,
            defval: bias === 'bearish' ? '#F23645' : '#2962FF',
            minval: '',
            maxval: '',
            options: [],
            group: '',
            tooltip: '',
            display: true,
        },
    ])
}

export function buildPatternDefaultInputValues(
    indicator?: Indicator,
    scriptInputs?: IndicatorInput[],
): Record<string, string> {
    const values: Record<string, string> = {}
    const fields = isCandlestickPatternIndicator(indicator)
        ? ensureCandlestickPatternConfigurationFields(indicator, scriptInputs)
        : getPatternConfigurationFields(indicator, scriptInputs)
    fields.forEach((field) => {
        values[field.code] = String(field.defval ?? '')
    })
    return values
}

export function findPatternLabelColorInput(
    indicator?: Indicator,
    scriptInputs?: IndicatorInput[],
): IndicatorInput | undefined {
    return ensureCandlestickPatternConfigurationFields(indicator, scriptInputs)
        .find(isPatternLabelColorInput)
}

export function isPatternLabelColorInput(input: Pick<IndicatorInput, 'code'>): boolean {
    return input.code === PATTERN_LABEL_COLOR_INPUT_CODE
}

export function getPatternLabelColorInputTitle(indicator?: Indicator): string {
    const bias = getCandlestickPatternBias(indicator)
    return bias === 'bearish' ? 'Label Color Bearish' : 'Label Color Bullish'
}

export function resolvePatternConfigurationInputTitle(
    input: Pick<IndicatorInput, 'code' | 'title'>,
    indicator?: Indicator,
): string {
    if (isPatternLabelColorInput(input)) {
        return getPatternLabelColorInputTitle(indicator)
    }
    return input.title
}

export function getCandlestickPatternBias(indicator?: Indicator, code?: string): 'bullish' | 'bearish' | undefined {
    const normalized = normalizeIndicatorCode(code ?? indicator?.code)
    const name = `${indicator?.name ?? ''} ${indicator?.shortName ?? ''}`
    if (normalized.startsWith('BEARISH_') || /\bbear/i.test(name)) return 'bearish'
    if (indicator && isCandlestickPatternIndicator(indicator)) return 'bullish'
    if (code && /\bbear/i.test(normalized)) return 'bearish'
    return indicator || code ? 'bullish' : undefined
}

function extractShowPriceTarget(settings?: Record<string, unknown>): boolean | undefined {
    if (!settings) return undefined
    const nested = (settings as { settings?: Record<string, unknown> }).settings ?? settings
    if ('showPriceTarget' in nested) return Boolean(nested.showPriceTarget)
    return undefined
}

export function patternSupportsPriceTarget(
    indicator?: Indicator,
    drawingSettings?: Record<string, unknown>,
): boolean {
    const fromOutputSettings = extractShowPriceTarget(drawingSettings)
    if (fromOutputSettings != null) return fromOutputSettings
    if (!indicator) return false
    const output = indicator.outputs.find((item) => item.drawingType === 'chartPatternOverlay')
    return extractShowPriceTarget(output?.drawingSettings as Record<string, unknown> | undefined) ?? false
}

export function buildPatternDefaultOutputValues(
    seriesIds: string[],
    outputValues: Record<string, SeriesOutput>,
    indicator?: Indicator,
): Record<string, SeriesOutput> {
    const seriesId = seriesIds[0]
    const current = seriesId ? outputValues[seriesId] : undefined
    if (!seriesId || !current || !indicator) return { ...outputValues }

    const indicatorOutput = indicator.outputs.find((item) => item.drawingType === 'chartPatternOverlay')
    if (!indicatorOutput) return { ...outputValues }

    const catalogSettings = getPatternDrawingSettingsMap(indicatorOutput.drawingSettings)
    const currentSettings = getPatternDrawingSettingsMap(current.drawingSettings)
    const mergedSettings = { ...catalogSettings, ...currentSettings }

    return {
        ...outputValues,
        [seriesId]: {
            ...current,
            drawingSettings: { settings: mergedSettings },
            colors: indicatorOutput.colors ?? current.colors,
        },
    }
}

export function usesStructuredWedgePatternLayout(drawingSettings?: Record<string, unknown>): boolean {
    const raw = (drawingSettings as { settings?: Record<string, unknown> })?.settings ?? drawingSettings ?? {}
    return 'showPatternLines' in raw || 'showTrendlines' in raw
}

export function usesTrianglePatternLayout(drawingSettings?: Record<string, unknown>): boolean {
    const raw = (drawingSettings as { settings?: Record<string, unknown> })?.settings ?? drawingSettings ?? {}
    return /triangle/i.test(String(raw.patternTitle ?? ''))
}
